import React, { useState } from 'react';
import { X, AlertTriangle, Lock } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import { useLang } from '../../contexts/LanguageContext';

/**
 * EMZI NEXUS V6.3 - Password Confirmation Modal
 * Used for confirming dangerous operations like permanent delete
 */
const PasswordConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmPassword = 'emzi4111',
  selectedCount = 0
}) => {
  const t = useLang();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (password !== confirmPassword) {
      setError(t('admin.users.passwordIncorrect') || 'Incorrect password');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err.message || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={handleClose}
      >
        {/* Modal */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: EMZI_RADIUS.lg,
            boxShadow: EMZI_SHADOWS.xl,
            width: '100%',
            maxWidth: '450px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${EMZI_COLORS.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertTriangle size={20} color="#dc2626" />
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '700',
                color: EMZI_COLORS.textPrimary
              }}>
                {title || t('admin.users.permanentDelete.confirmTitle')}
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                background: 'none',
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                padding: '8px',
                color: EMZI_COLORS.textSecondary,
                opacity: isSubmitting ? 0.5 : 1
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '24px' }}>
            {/* Warning Message */}
            <div style={{
              padding: '16px',
              backgroundColor: '#fef2f2',
              border: '2px solid #fecaca',
              borderRadius: EMZI_RADIUS.md,
              marginBottom: '24px'
            }}>
              <p style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#991b1b'
              }}>
                {message || t('admin.users.permanentDelete.warning')}
              </p>
              <p style={{
                margin: 0,
                fontSize: '13px',
                color: '#7f1d1d',
                lineHeight: '1.5'
              }}>
                {t('admin.users.permanentDelete.warningDetail')
                  ?.replace('{count}', selectedCount) || 
                  `You are about to permanently delete ${selectedCount} user(s). This action cannot be undone.`}
              </p>
            </div>

            {/* Password Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: EMZI_COLORS.textPrimary
              }}>
                <Lock size={16} style={{ 
                  display: 'inline-block', 
                  verticalAlign: 'middle', 
                  marginRight: '6px' 
                }} />
                {t('admin.users.permanentDelete.enterPassword') || 'Enter password to confirm'}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && password && !isSubmitting) {
                    handleSubmit();
                  }
                }}
                placeholder="••••••••"
                disabled={isSubmitting}
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `2px solid ${error ? '#dc2626' : EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  letterSpacing: '2px',
                  backgroundColor: isSubmitting ? '#f9fafb' : 'white',
                  cursor: isSubmitting ? 'not-allowed' : 'text'
                }}
              />
              {error && (
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: '13px',
                  color: '#dc2626',
                  fontWeight: '500'
                }}>
                  ⚠️ {error}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  border: `1px solid ${EMZI_COLORS.border}`,
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  color: EMZI_COLORS.textPrimary,
                  backgroundColor: 'white',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {t('admin.users.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!password || isSubmitting}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: EMZI_RADIUS.md,
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#dc2626',
                  cursor: (!password || isSubmitting) ? 'not-allowed' : 'pointer',
                  opacity: (!password || isSubmitting) ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {isSubmitting 
                  ? (t('admin.users.deleting') || 'Deleting...') 
                  : (t('admin.users.permanentDelete.confirm') || 'Permanent Delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PasswordConfirmModal;
